import { useState } from 'react';
import { createPet, updatePet } from '../services/pets';
import { AVATAR_PALETTE } from '../utils/avatarColors';
import Avatar from './Avatar';

/**
 * PetFormModal — create or edit a pet (a login-less wish-list owner).
 *
 * Mirrors GroupFormModal's structure and the shared color-picker pattern: name,
 * a free-text species (with quick suggestions), an optional avatar color from
 * the brand palette, and an optional birthday. A live avatar preview shows how
 * the pet's badge will look with the chosen color.
 *
 * Props:
 *  - pet:      the pet when editing, null/undefined when creating
 *  - groupId:  the group to create the pet in (only used on create)
 *  - onClose:  close without saving
 *  - onSaved(savedPet, isEdit): called with the API's saved pet
 */

const SPECIES_SUGGESTIONS = ['Dog', 'Cat', 'Bird', 'Rabbit', 'Fish', 'Hamster', 'Horse', 'Reptile'];

export default function PetFormModal({ pet, groupId, onClose, onSaved }) {
    const isEdit = !!pet;

    const [name, setName] = useState(pet?.name || '');
    const [species, setSpecies] = useState(pet?.species || '');
    const [color, setColor] = useState(pet?.avatar_color || null);
    // birthday / gotcha_day can arrive as a full ISO datetime (the API casts them
    // to dates) — keep just the YYYY-MM-DD part for the <input type="date">.
    const [birthday, setBirthday] = useState(pet?.birthday ? String(pet.birthday).slice(0, 10) : '');
    const [gotchaDay, setGotchaDay] = useState(pet?.gotcha_day ? String(pet.gotcha_day).slice(0, 10) : '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const submit = async (e) => {
        e.preventDefault();
        if (saving) return;
        setSaving(true);
        setError('');
        const payload = {
            name: name.trim(),
            species: species.trim() || null,
            avatar_color: color || null,
            birthday: birthday || null,
            gotcha_day: gotchaDay || null,
        };
        try {
            const saved = isEdit ? await updatePet(pet.id, payload) : await createPet(groupId, payload);
            onSaved(saved, isEdit);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save the pet.');
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
                <h3 className="text-lg font-semibold mb-4">{isEdit ? 'Edit Pet' : 'Add a Pet'}</h3>

                {/* Live preview of the pet's avatar badge with the chosen color. */}
                <div className="flex items-center gap-3 mb-4">
                    <Avatar name={name || '🐾'} color={color} seed={pet?.id ?? 0} size={48} />
                    <div className="text-sm text-gray-500">
                        <p className="font-medium text-gray-800">{name.trim() || 'Your pet'}</p>
                        <p>{species.trim() || 'Add a species below'}</p>
                    </div>
                </div>

                <form onSubmit={submit}>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                        <input
                            type="text"
                            required
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g., Rex"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Species (optional)</label>
                        <div className="flex flex-wrap gap-1.5 mb-2">
                            {SPECIES_SUGGESTIONS.map((s) => (
                                <button
                                    type="button"
                                    key={s}
                                    onClick={() => setSpecies(s)}
                                    className={`px-2.5 h-8 rounded-md text-sm ${species === s ? 'ring-2 ring-blue-500 bg-blue-50' : 'bg-gray-100 hover:bg-gray-200'}`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                        <input
                            type="text"
                            value={species}
                            maxLength={50}
                            onChange={(e) => setSpecies(e.target.value)}
                            placeholder="or type one, e.g. Ferret"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Color (optional)</label>
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={() => setColor(null)}
                                title="Auto"
                                className={`w-8 h-8 rounded-full border border-gray-300 text-xs text-gray-500 flex items-center justify-center ${!color ? 'ring-2 ring-gray-400' : ''}`}
                            >
                                A
                            </button>
                            {AVATAR_PALETTE.map((sw) => (
                                <button
                                    type="button"
                                    key={sw.hex}
                                    onClick={() => setColor(sw.hex)}
                                    title={sw.name}
                                    style={{ backgroundColor: sw.hex }}
                                    className={`w-8 h-8 rounded-full ${color === sw.hex ? 'ring-2 ring-offset-1 ring-gray-500' : ''}`}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Birthday (optional)</label>
                        <input
                            type="date"
                            value={birthday}
                            onChange={(e) => setBirthday(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Gotcha day (optional)</label>
                        <input
                            type="date"
                            value={gotchaDay}
                            onChange={(e) => setGotchaDay(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="text-xs text-gray-400 mt-1">The day they joined your family.</p>
                    </div>

                    {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

                    <div className="flex gap-2 justify-end">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md">
                            Cancel
                        </button>
                        <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                            {saving ? 'Saving…' : isEdit ? 'Save' : 'Add Pet'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
